import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { EnvelopeIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const EmailChangeModal = ({
  currentEmail,
  unconfirmedEmail,
  updateEmailUrl,
  resendConfirmationUrl,
  cancelEmailChangeUrl,
  confirmationSentAt,
  isAdmin = false,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <EmailTriggerButton
        currentEmail={currentEmail}
        unconfirmedEmail={unconfirmedEmail}
        onClick={() => setOpen(true)}
      />
      <Modal
        isOpen={open}
        setOpen={setOpen}
        currentEmail={currentEmail}
        unconfirmedEmail={unconfirmedEmail}
        updateEmailUrl={updateEmailUrl}
        resendConfirmationUrl={resendConfirmationUrl}
        cancelEmailChangeUrl={cancelEmailChangeUrl}
        confirmationSentAt={confirmationSentAt}
        isAdmin={isAdmin}
      />
    </>
  );
};

function EmailTriggerButton({ currentEmail, unconfirmedEmail, onClick }) {
  return (
    <div className="space-y-2">
      <dt className="text-sm font-medium text-gray-500">Email address</dt>
      <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
        <span>{currentEmail}</span>
        <button
          type="button"
          onClick={onClick}
          className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
        >
          Change
        </button>
      </dd>
      {unconfirmedEmail && (
        <div className="mt-2 rounded-md bg-amber-50 p-3 border border-amber-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-700">
                Pending confirmation: <span className="font-medium">{unconfirmedEmail}</span>
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Check your inbox for a confirmation link
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Modal({
  isOpen,
  setOpen,
  currentEmail,
  unconfirmedEmail,
  updateEmailUrl,
  resendConfirmationUrl,
  cancelEmailChangeUrl,
  confirmationSentAt,
  isAdmin,
}) {
  const cancelButtonRef = useRef(null);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(updateEmailUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']")?.content,
        },
        body: JSON.stringify({ user: { email: newEmail } }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setNewEmail('');
        // Reload after a short delay to show success message
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setError(data.error || data.errors?.email?.[0] || 'Failed to update email');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setLoading(true);
    setError(null);
    setResendSuccess(false);

    try {
      const response = await fetch(resendConfirmationUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']")?.content,
        },
      });

      if (response.ok) {
        setResendSuccess(true);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to resend confirmation');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEmailChange = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(cancelEmailChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']")?.content,
        },
      });

      if (response.ok) {
        window.location.reload();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to cancel email change');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
    setSuccess(false);
    setResendSuccess(false);
    setNewEmail('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <Transition show={isOpen}>
      <Dialog as="div" className="relative z-50" initialFocus={cancelButtonRef} onClose={handleClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                {success ? (
                  <SuccessState onClose={handleClose} />
                ) : unconfirmedEmail ? (
                  <PendingConfirmationState
                    currentEmail={currentEmail}
                    unconfirmedEmail={unconfirmedEmail}
                    confirmationSentAt={confirmationSentAt}
                    formatDate={formatDate}
                    loading={loading}
                    error={error}
                    resendSuccess={resendSuccess}
                    onResend={handleResendConfirmation}
                    onCancel={handleCancelEmailChange}
                    onClose={handleClose}
                    cancelButtonRef={cancelButtonRef}
                    isAdmin={isAdmin}
                  />
                ) : (
                  <ChangeEmailState
                    currentEmail={currentEmail}
                    newEmail={newEmail}
                    setNewEmail={setNewEmail}
                    loading={loading}
                    error={error}
                    onSubmit={handleSubmit}
                    onClose={handleClose}
                    cancelButtonRef={cancelButtonRef}
                    isAdmin={isAdmin}
                  />
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

function SuccessState({ onClose }) {
  return (
    <div className="text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
        <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
      </div>
      <div className="mt-3 sm:mt-5">
        <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900">
          Confirmation Email Sent
        </DialogTitle>
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Please check your inbox and click the confirmation link to complete the email change.
          </p>
        </div>
      </div>
      <div className="mt-5">
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
}

function PendingConfirmationState({
  currentEmail,
  unconfirmedEmail,
  confirmationSentAt,
  formatDate,
  loading,
  error,
  resendSuccess,
  onResend,
  onCancel,
  onClose,
  cancelButtonRef,
  isAdmin,
}) {
  return (
    <div>
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
        <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" aria-hidden="true" />
      </div>
      <div className="mt-3 text-center sm:mt-5">
        <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900">
          Email Change Pending
        </DialogTitle>
        <div className="mt-4 text-left space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Current Email</p>
              <p className="mt-1 text-sm text-gray-900">{currentEmail}</p>
            </div>
            <div className="border-t border-gray-200 pt-3">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Pending New Email</p>
              <p className="mt-1 text-sm text-gray-900 font-medium">{unconfirmedEmail}</p>
            </div>
            {confirmationSentAt && (
              <div className="border-t border-gray-200 pt-3">
                <p className="text-xs text-gray-500">
                  Confirmation sent: {formatDate(confirmationSentAt)}
                </p>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-500">
            {isAdmin
              ? "A confirmation email was sent to the new address. The member must click the link to complete the change."
              : "A confirmation email was sent to your new address. Click the link in that email to complete the change."
            }
          </p>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {resendSuccess && (
            <div className="rounded-md bg-green-50 p-3">
              <p className="text-sm text-green-700">Confirmation email resent successfully!</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 sm:mt-6 space-y-3">
        <button
          type="button"
          disabled={loading}
          onClick={onResend}
          className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:opacity-50"
        >
          {loading ? 'Sending...' : 'Resend Confirmation Email'}
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={onCancel}
          className="inline-flex w-full justify-center rounded-md border border-red-300 bg-white px-4 py-2 text-base font-medium text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:text-sm disabled:opacity-50"
        >
          Cancel Email Change
        </button>
        <button
          type="button"
          ref={cancelButtonRef}
          onClick={onClose}
          className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function ChangeEmailState({
  currentEmail,
  newEmail,
  setNewEmail,
  loading,
  error,
  onSubmit,
  onClose,
  cancelButtonRef,
  isAdmin,
}) {
  return (
    <div>
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100">
        <EnvelopeIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
      </div>
      <div className="mt-3 text-center sm:mt-5">
        <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900">
          Change Email Address
        </DialogTitle>
        <form onSubmit={onSubmit} className="mt-4 text-left space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Current Email
            </label>
            <p className="mt-1 text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
              {currentEmail}
            </p>
          </div>

          <div>
            <label htmlFor="new-email" className="block text-sm font-medium text-gray-700">
              New Email Address
            </label>
            <input
              type="email"
              id="new-email"
              name="new-email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Enter new email address"
            />
          </div>

          <div className="rounded-md bg-blue-50 p-3">
            <p className="text-sm text-blue-700">
              {isAdmin
                ? "A confirmation email will be sent to the new address. The member's current email will remain active until they confirm the change."
                : "A confirmation email will be sent to your new address. Your current email will remain active until you confirm the change."
              }
            </p>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
            <button
              type="submit"
              disabled={loading || !newEmail}
              className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Email'}
            </button>
            <button
              type="button"
              ref={cancelButtonRef}
              onClick={onClose}
              className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EmailChangeModal;

